import { styled } from '@mui/material/styles';
import { Checkbox, FormControlLabel } from '@mui/material';
import { CheckboxSize, CheckboxValidationState } from './Checkbox.types';
import { designTokens } from '../../tokens';

/**
 * Size-specific styles for checkboxes
 */
const getSizeStyles = (size: CheckboxSize) => {
  switch (size) {
    case 'sm':
      return {
        '& .MuiCheckbox-root': {
          padding: designTokens.spacing[1],
          '& svg': {
            fontSize: '1rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.xs,
        },
      };
    case 'md':
      return {
        '& .MuiCheckbox-root': {
          padding: designTokens.spacing[2],
          '& svg': {
            fontSize: '1.25rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.sm,
        },
      };
    case 'lg':
      return {
        '& .MuiCheckbox-root': {
          padding: designTokens.spacing[2],
          '& svg': {
            fontSize: '1.5rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.base,
        },
      };
    default:
      return {};
  }
};

/**
 * Validation state styles
 */
const getValidationStyles = (validationState: CheckboxValidationState) => {
  const tokens = designTokens.colors;

  switch (validationState) {
    case 'error':
      return {
        '& .MuiCheckbox-root': {
          color: tokens.error[500],
          '&.Mui-checked': {
            color: tokens.error[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.error[700],
        },
      };

    case 'success':
      return {
        '& .MuiCheckbox-root': {
          color: tokens.success[500],
          '&.Mui-checked': {
            color: tokens.success[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.success[700],
        },
      };

    case 'warning':
      return {
        '& .MuiCheckbox-root': {
          color: tokens.warning[500],
          '&.Mui-checked': {
            color: tokens.warning[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.warning[700],
        },
      };

    case 'default':
    default:
      return {
        '& .MuiCheckbox-root': {
          color: tokens.neutral[600],
          '&.Mui-checked': {
            color: tokens.primary[600],
          },
          '&:hover': {
            backgroundColor: `${tokens.primary[50]}40`,
          },
        },
      };
  }
};

/**
 * Styled Checkbox component
 */
export const StyledCheckbox = styled(Checkbox)(() => ({
  transition: `all ${designTokens.transitions.duration.fast} ${designTokens.transitions.timing.easeInOut}`,

  '&.Mui-disabled': {
    color: designTokens.colors.neutral[400],
  },

  // Indeterminate state
  '&.MuiCheckbox-indeterminate': {
    color: designTokens.colors.primary[600],
  },
}));

/**
 * Styled FormControlLabel wrapper
 */
export const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) =>
    !['checkboxSize', 'validationState'].includes(prop as string),
})<{
  checkboxSize: CheckboxSize;
  validationState: CheckboxValidationState;
}>(({ checkboxSize, validationState }) => ({
  ...getSizeStyles(checkboxSize),
  ...getValidationStyles(validationState),

  margin: 0,
  fontFamily: designTokens.typography.fontFamily.primary,

  '& .MuiFormControlLabel-label': {
    fontWeight: designTokens.typography.fontWeight.regular,
    lineHeight: designTokens.typography.lineHeight.normal,
    userSelect: 'none',
  },

  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: designTokens.colors.neutral[400],
  },
}));
